import { useEffect, useRef, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { Button } from '../../../components';
import { useLang, type Lang } from '../../../i18n';
import { get } from '../../../services/api';
import { useLeadModal } from '../../../contexts/LeadModalContext';
import useIntersectionFadeIn from '../../../hooks/useIntersectionFadeIn';

interface FaqItem { id: string | number; question: string; answer: string }
interface FaqCategory { slug: string; title: string; items: FaqItem[] }
type FaqApi = FaqCategory[] | { items?: FaqItem[]; results?: FaqItem[] };

const LANG_MAP: Record<Lang, string> = { 'uz-Latn': 'uz_lat', 'uz-Cyrl': 'uz_cyr', ru: 'ru', en: 'en' };

export default function FaqSection() {
  const { t, lang } = useLang();
  const { openModal } = useLeadModal();
  const [items, setItems] = useState<FaqItem[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const rootRef = useRef<HTMLElement>(null);
  const visible = useIntersectionFadeIn(rootRef);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await get<FaqApi>(`/faq/?lang=${LANG_MAP[lang]}`);
        if (cancel) return;
        let flat: FaqItem[] = [];
        if (Array.isArray(res)) {
          flat = res.flatMap((c) => c.items || []);
        } else if (res) {
          flat = res.items || res.results || [];
        }
        setItems(flat.slice(0, 6));
      } catch {
        if (!cancel) setItems([]);
      }
    })();
    return () => { cancel = true; };
  }, [lang]);

  const toggle = (id: string) => setOpenId((cur) => (cur === id ? null : id));

  if (!items.length) return null;

  return (
    <section ref={rootRef} className={`section faq scroll-fade-in${visible ? ' scroll-fade-in--visible' : ''}`}>
      <div className="container faq__grid">
        <aside className="faq__aside">
          <span className="eyebrow">{t('home.faq.eyebrow')}</span>
          <h2 className="faq__title">{t('home.faq.title')}</h2>
          <p className="lead faq__lead">{t('home.faq.lead')}</p>
          <Button
            variant="primary"
            size="lg"
            onClick={() => openModal()}
            iconLeft={<MessageSquare size={18} />}
          >
            {t('home.faq.cta')}
          </Button>
        </aside>

        <div className="faq__list">
          {items.map((it) => {
            const id = String(it.id);
            const isOpen = openId === id;
            return (
              <div key={id} className={`faq__item${isOpen ? ' faq__item--open' : ''}`}>
                <button
                  type="button"
                  className="faq__q"
                  aria-expanded={isOpen}
                  onClick={() => toggle(id)}
                >
                  <span className="faq__q-text">{it.question}</span>
                  <span className="faq__q-icon" aria-hidden="true">{isOpen ? '–' : '+'}</span>
                </button>
                {isOpen ? <div className="faq__a">{it.answer}</div> : null}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
